/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  DOCUMENT,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  booleanAttribute,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import clsx from 'clsx';
import { createSearchIndex, getAllSearchEntries, searchInIndex } from '@pedix-workspace/utils';
import {
  IconCaretDownComponent,
  IconCloseComponent,
  IconInfoComponent,
  IconLoadingComponent,
} from '@pedix-workspace/angular-ui-icons';
import {
  WithDefaultValidatorProps,
  composeValidatorFn,
  hasChangedAnyDefaultValidatorProps,
} from '../utils/defaultValidator';
import { CdkListbox, CdkOption, ListboxValueChangeEvent } from '@angular/cdk/listbox';
import { NgbDropdown, NgbDropdownAnchor, NgbDropdownMenu } from '@ng-bootstrap/ng-bootstrap';
import { UiFormReactiveConfigService } from '../form-reactive-config.service';
import { filter, fromEvent } from 'rxjs';
import { SelectBoxOptionKeyPipe } from './select-box-option-key.pipe';
import { isEqual } from 'lodash-es';

let INPUT_UID = 0;

export type SelectBoxLabelSize = 'xs' | 'sm' | 'md';

export type SelectBoxValueType = string | undefined | null;
export type SelectBoxOptionGroup = {
  key: string;
  label: string;
  options: any[];
};
export type SelectBoxGroupByFn = (option: any) => string;

@Component({
  selector: 'pxw-select-box',
  templateUrl: './select-box.component.html',
  styleUrl: './select-box.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SelectBoxComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: SelectBoxComponent,
    },
  ],
  imports: [
    NgClass,
    NgStyle,
    IconInfoComponent,
    IconCloseComponent,
    IconCaretDownComponent,
    CdkListbox,
    CdkOption,
    FormsModule,
    NgbDropdownMenu,
    NgbDropdownAnchor,
    NgTemplateOutlet,
    SelectBoxOptionKeyPipe,
    IconLoadingComponent,
  ],
  hostDirectives: [NgbDropdown],
})
export class SelectBoxComponent
  implements
    OnInit,
    OnDestroy,
    OnChanges,
    ControlValueAccessor,
    Validator,
    WithDefaultValidatorProps
{
  @ViewChild(CdkListbox) listbox: CdkListbox;
  @ViewChild(NgbDropdownMenu) dropdownMenu: NgbDropdownMenu;
  @ViewChild(NgbDropdownAnchor) dropdownAnchor: NgbDropdownAnchor;
  @ViewChildren(CdkOption) optionsRef: QueryList<CdkOption>;
  @ContentChild('selectedItemTemplate') selectedItemTemplate?: TemplateRef<{ $implicit: any }>;
  @ContentChild('optionItemTemplate') optionItemTemplate?: TemplateRef<{ $implicit: any }>;
  @ContentChild('groupHeaderTemplate')
  groupHeaderTemplate?: TemplateRef<{ $implicit: SelectBoxOptionGroup }>;
  @ContentChild('emptyOptionsTemplate') emptyOptionsTemplate?: TemplateRef<void>;
  @ContentChild('addOptionTemplate') addOptionTemplate?: TemplateRef<{ $implicit: string }>;
  @ContentChild('dropdownTemplate') dropdownTemplate?: TemplateRef<{
    $implicit: any[];
    options: any[];
    onSelectedOption: SelectBoxComponent['onSelectedOption'];
  }>;

  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  @Input({ required: true }) name: string;
  @Input() options: any[];
  readonly groupBy = input<string | SelectBoxGroupByFn | undefined>();
  readonly groupByLabel = input<string | SelectBoxGroupByFn | undefined>();
  @Input() labelKey: string;
  @Input() valueKey: string;
  @Input() label: string;
  @Input() labelSize: SelectBoxLabelSize = 'sm';
  @Input() helper: string | null;
  @Input() placeholder = '';
  @Input() messages: { [key: string]: string } = {};
  @Input() displayErrors = false;
  @Input() maxDropdownHeight = 250;
  @Input() displayClear = false;
  @Input() searchable = false;
  @Input() emptyOptionsText = 'No options to display';
  /**
   * Cuando hay opciones pero el término las descartó a todas. Sin definir cae en
   * `emptyOptionsText`, que es lo que veían todos los consumidores hasta ahora.
   */
  @Input() noResultsText = '';
  @Input() hideDropdownMenu = false;
  @Input() allowNewValues = true;
  @Input() searchTextTransformFn = (searchText: string) => searchText;
  /**
   * Modo estrictamente **controlado**: al elegir una opción el componente NO se aplica el valor a sí
   * mismo, sólo lo emite; si el padre lo escribe de vuelta, el resultado converge al de siempre. Y
   * tampoco selecciona sola al perder el foco, que sería emitir algo que nadie pidió.
   *
   * Es sólo la disciplina del `ControlValueAccessor`. Lo que pasa con el dropdown al elegir es otra
   * decisión: `closeOnSelect`.
   *
   * Arranca en `false` porque los consumidores actuales dependen del auto-aplicado.
   */
  @Input() forceControlled = false;
  /**
   * Si al elegir una opción se cierra la lista. En `false` queda abierta y conserva el texto
   * escrito, que es lo que permite usarla para elegir varias seguidas ("elegir y agregar"). Al
   * cerrarse, el texto vuelve a reflejar el valor como siempre.
   */
  @Input() closeOnSelect = true;

  @Input() dropdownContainer: NgbDropdown['container'] = null;
  @Input() dropdownPlacement: NgbDropdown['placement'] = [
    'bottom-start',
    'bottom-end',
    'top-start',
    'top-end',
  ];

  @Input({ transform: booleanAttribute }) required?: boolean;
  @Input({ transform: booleanAttribute }) requiredTrue?: boolean;
  @Input({ transform: booleanAttribute }) email?: boolean;
  @Input() minLength?: number;
  @Input() maxLength?: number;
  @Input() pattern?: RegExp | readonly RegExp[];

  @Input({ transform: booleanAttribute }) loading = false;

  @Output() opened = new EventEmitter<void>();
  @Output() searchTextChange = new EventEmitter<string>();
  @Output() addOption = new EventEmitter<string>();

  selectedKeyLabel = '';
  clearSearchOnUndefinedOptions = true;

  #value = signal<SelectBoxValueType>(null);
  #searchText = signal<string>('');
  #displayAllOptions = signal<boolean>(false);
  #optionsSignal = signal<any[]>([]);

  private injector = inject(Injector);
  private dropdown = inject(NgbDropdown);
  private ngZone = inject(NgZone);
  private document = inject(DOCUMENT);
  private rootEl: ElementRef<HTMLElement> = inject(ElementRef);
  private cd = inject(ChangeDetectorRef);
  private selectBoxOptionKeyPipe = inject(SelectBoxOptionKeyPipe);

  protected inputId: string;
  protected isDisabled: boolean;
  protected clickOutsideListener: () => void;

  private onChange: (value: SelectBoxValueType) => void;
  private onTouched: () => void;
  private onValidatorChange: () => void;
  private validatorFn: ValidatorFn | null;
  private formReactiveConfigService = inject(UiFormReactiveConfigService);
  private destroyRef = inject(DestroyRef);

  @HostBinding('class') get classNames() {
    return clsx('ui-input', `ui-select-box`, {
      'ui-input--disabled': this.isDisabled,
      'ui-input--with-helper': this.helper,
      'ui-input--with-errors': !this.isValid,
      'ui-input--with-groups': !!this.groupBy(),
    });
  }

  get formControl() {
    const ngControl = this.injector.get(NgControl, null);

    if (ngControl) {
      return ngControl.control as FormControl;
    }
    return null;
  }

  get isRequired() {
    return this.formControl?.hasValidator(Validators.required) || this.required === true;
  }

  get isValid() {
    return this.formControl?.valid;
  }

  get isTouched() {
    return this.formControl?.touched;
  }

  get errorEntries(): [string, any][] {
    return Object.entries(this.formControl?.errors || {});
  }

  get shouldDisplayErrors() {
    return (this.displayErrors || this.isTouched) && !this.isValid && !this.isDisabled;
  }

  get filterText() {
    if (this.searchable && !this.displayAllOptions) {
      return this.searchText;
    }
    return '';
  }

  get emptyOptionsMessage(): string {
    const filteredOutEverything = (this.options?.length ?? 0) > 0;

    return filteredOutEverything && this.noResultsText ? this.noResultsText : this.emptyOptionsText;
  }

  get value(): SelectBoxValueType {
    return this.#value();
  }

  get searchText(): string {
    return this.#searchText();
  }

  get displayAllOptions(): boolean {
    return this.#displayAllOptions();
  }

  get selectedOption() {
    return this.options.find(option => {
      return this.value === this.selectBoxOptionKeyPipe.transform(option, this.valueKey);
    });
  }

  get selectedOptionLabel(): string {
    const selectedOption = this.selectedOption;

    if (!selectedOption) {
      return '';
    }
    return (this.selectedKeyLabel = this.selectBoxOptionKeyPipe.transform(
      selectedOption,
      this.labelKey,
    ));
  }

  /**
   * El índice se arma UNA vez por cambio de opciones, no en cada tecla: escribir sólo re-lee el
   * término. Se indexa el texto **visible** (`labelKey`), que es por lo que el usuario busca;
   * `valueKey` queda de fallback para las opciones donde valor y texto son lo mismo.
   */
  private readonly searchIndex = computed(() => {
    const options = this.#optionsSignal();
    const key = this.labelKey || this.valueKey;
    const groupBy = this.groupBy();
    const groupByLabel = this.groupByLabel();

    return createSearchIndex(options, {
      indexedKeywordsFn: option => [
        String((key ? option?.[key] : option) ?? ''),
        ...(groupBy ? [this.resolveGroupLabel(option, groupBy, groupByLabel)] : []),
      ],
    });
  });

  private resolveGroupKey(option: any, groupBy: string | SelectBoxGroupByFn): string {
    const rawKey =
      typeof groupBy === 'function'
        ? groupBy(option)
        : this.selectBoxOptionKeyPipe.transform(option, groupBy);

    return rawKey == null ? '' : String(rawKey);
  }

  private resolveGroupLabel(
    option: any,
    groupBy: string | SelectBoxGroupByFn,
    groupByLabel: string | SelectBoxGroupByFn | undefined,
  ): string {
    if (!groupByLabel) {
      return this.resolveGroupKey(option, groupBy);
    }
    const rawLabel =
      typeof groupByLabel === 'function'
        ? groupByLabel(option)
        : this.selectBoxOptionKeyPipe.transform(option, groupByLabel);

    return rawLabel == null ? '' : String(rawLabel);
  }

  /**
   * Busca por palabras sueltas y en cualquier orden: "ha me" encuentra "Harina 000 · Paquete
   * medio". Además normaliza acentos, así que "azucar" encuentra "Azúcar".
   */
  readonly filteredOptions = computed<any[]>(() => {
    const index = this.searchIndex();
    const term = this.filterText;

    return term.trim() ? searchInIndex(index, term) : getAllSearchEntries(index);
  });

  /**
   * Se agrupa lo YA filtrado, no al revés: así hay un solo índice —que no depende del
   * agrupamiento— y los grupos que quedan sin opciones simplemente no aparecen, sin necesidad de
   * descartarlos después.
   */
  readonly groupedOptions = computed<SelectBoxOptionGroup[]>(() => {
    const groupBy = this.groupBy();
    const groupByLabel = this.groupByLabel();
    const options = this.filteredOptions();

    if (!groupBy || options.length === 0) {
      return [];
    }
    const groups = new Map<string, any[]>();
    const labels = new Map<string, string>();

    for (const option of options) {
      const key = this.resolveGroupKey(option, groupBy);
      const groupOptions = groups.get(key);

      if (!labels.has(key)) {
        labels.set(key, this.resolveGroupLabel(option, groupBy, groupByLabel));
      }
      if (groupOptions) {
        groupOptions.push(option);
      } else {
        groups.set(key, [option]);
      }
    }
    return Array.from(groups, ([key, options]) => ({
      key,
      label: labels.get(key) ?? key,
      options,
    }));
  });

  ngOnInit(): void {
    this.inputId = `select-box-${++INPUT_UID}`;

    this.dropdown.autoClose = false;
    this.dropdown.container = this.dropdownContainer;
    this.dropdown.dropdownClass = 'ui-dropdown-layer';
    this.dropdown.placement = this.dropdownPlacement;

    this.dropdown.openChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isOpen => {
      if (isOpen) {
        // Abrir muestra todas las opciones… salvo en el modo "elegir varias", donde el texto
        // escrito sobrevive a la selección y la lista tiene que seguir respetándolo.
        this.#displayAllOptions.set(this.closeOnSelect);
        this.opened.emit();
      } else {
        this.onDropdownClose();
      }
    });
    this.ngZone.runOutsideAngular(() =>
      fromEvent(this.document, 'click')
        .pipe(
          filter(() => this.dropdown.isOpen()),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(event => {
          if (!event.composedPath().includes(this.rootEl.nativeElement)) {
            this.dropdown.close();

            this.cd.detectChanges();
          }
        }),
    );
  }

  ngOnDestroy(): void {
    if (this.dropdown.isOpen()) {
      this.dropdown.close();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (hasChangedAnyDefaultValidatorProps(changes)) {
      this.validatorFn = composeValidatorFn(this);

      if (this.formControl) {
        this.formControl.updateValueAndValidity();
      }
    }
    if (changes['options']) {
      this.#optionsSignal.set(this.options ?? []);

      if (
        this.value &&
        !isEqual(changes['options'].previousValue, changes['options'].currentValue)
      ) {
        this.writeValue(this.value);
      }
    }
  }

  writeValue(value: SelectBoxValueType) {
    this.#value.set(value ?? null);

    if (this.value !== null) {
      const selectedOptionLabel = this.selectedOptionLabel;

      if (selectedOptionLabel) {
        this.selectedKeyLabel = selectedOptionLabel;
      }
    } else {
      this.selectedKeyLabel = '';
    }
    if (this.searchable) {
      if (this.selectedKeyLabel) {
        this.updateSearchText(this.selectedKeyLabel);
      } else if (this.clearSearchOnUndefinedOptions) {
        this.updateSearchText('');
      }
    }
  }
  registerOnChange(onChange: (value: SelectBoxValueType) => void) {
    this.onChange = onChange;
  }
  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validate(control: AbstractControl): ValidationErrors | null {
    return this.validatorFn?.(control) || null;
  }
  registerOnValidatorChange(onValidatorChange: () => void): void {
    this.onValidatorChange = onValidatorChange;
  }
  // ListboxValueChangeEvent<SelectBoxValueType>
  onSelectedOption = ($event: ListboxValueChangeEvent<string>) => {
    const newValue = $event.value[0];

    // El listbox del CDK guarda su propia selección y clickear una opción ya elegida la ALTERNA:
    // el segundo click llega vacío. Con los consumidores clásicos no se nota, porque el valor queda
    // puesto en el campo; en "elegir y agregar" hay opciones que siguen en la lista después de
    // usarlas, y la segunda vez no pasaba nada. Se deselecciona para que cada click sea uno nuevo.
    if (this.forceControlled) {
      if (newValue == null) {
        return;
      }
      $event.listbox.setAllSelected(false);
    }
    if ($event.option?.element.classList.contains('ui-select__item--add')) {
      this.addOption.emit(newValue!);
    } else {
      this.setNewValue(newValue);
    }
    if (this.closeOnSelect) {
      this.dropdown.close();
    }
  };

  onOpenDropdown() {
    // NOTE: this workaround is to fix the issue with using NgbDropdown as a host directive
    // as it cannot find the "ContentChild" directive dependencies (as we are providing them in the view of this component)
    // this is why we assign them imperatively in the ngAfterViewInit hook
    (this.dropdown as any)._menu = this.dropdownMenu;
    (this.dropdown as any)._anchor = this.dropdownAnchor;

    this.dropdown.open();

    if (this.listbox) {
      setTimeout(() => {
        this.listbox.focus();
      });
      const searchInput = this.searchInput();

      if (searchInput) {
        setTimeout(() => {
          searchInput.nativeElement.focus();
        });
      }
    }
  }

  onClear() {
    this.updateSearchText('');
    this.setNewValue(null);
  }

  onSearchInputKeyUp($event: KeyboardEvent) {
    this.#displayAllOptions.set(false);

    if ($event.key === 'Escape') {
      this.dropdown.close();

      return;
    } else if ($event.key === 'Enter') {
      if (
        this.searchText !== this.value &&
        this.allowNewValues &&
        !this.valueIsInOptions(this.searchText)
      ) {
        this.addOption.emit(this.searchText);
      } else if (this.optionsRef.toArray()[0]?.getLabel() === this.searchText) {
        this.setNewValue(this.optionsRef.toArray()[0].value as SelectBoxValueType);
      }

      this.dropdown.close();

      return;
    }
    const newValue = (<HTMLInputElement>$event.target).value;

    if (!this.dropdown.isOpen()) {
      this.dropdown.open();
    }

    this.updateSearchText(newValue);
  }

  onSearchInputFocusIn($event: Event) {
    const input = $event.target as HTMLInputElement;

    setTimeout(() => {
      input.selectionStart = input.value.length;
      input.selectionEnd = input.value.length;
    });
  }

  onSearchInputFocusOut($event: Event) {
    if (
      this.searchText !== this.value &&
      this.allowNewValues &&
      !this.valueIsInOptions(this.searchText)
    ) {
      this.addOption.emit(this.searchText);
    } else if (
      // Controlado: emitir acá haría que salir del campo dispare una selección sin que nadie la
      // haya pedido — y en "elegir y agregar" eso agrega una fila sola.
      !this.forceControlled &&
      this.optionsRef.toArray()[0]?.getLabel() === this.searchText
    ) {
      this.setNewValue(this.optionsRef.toArray()[0].value as SelectBoxValueType);
    }
  }

  onDropdownClose() {
    if (
      this.searchable &&
      this.searchText !== this.selectedKeyLabel &&
      this.clearSearchOnUndefinedOptions
    ) {
      this.updateSearchText(this.selectedKeyLabel);
    }
  }

  protected setNewValue(newValue: SelectBoxValueType) {
    // Controlado: el valor lo decide el padre. Acá sólo se avisa.
    if (!this.forceControlled) {
      this.writeValue(newValue);
    }
    this.onChange(newValue);
    this.onValidatorChange();
    this.onTouched();
  }

  protected getErrorMessage([key, data]: [string, any]) {
    return this.messages?.[key] || this.formReactiveConfigService.getErrorMessage(key, data);
  }

  protected valueIsInOptions(value: string): boolean {
    return !!this.options.find(option => {
      return value === this.selectBoxOptionKeyPipe.transform(option, this.labelKey);
    });
  }

  private updateSearchText(searchText: string) {
    const transformedSearchText = this.searchTextTransformFn(searchText);

    this.#searchText.set(transformedSearchText);

    this.searchTextChange.emit(transformedSearchText);
  }
}
